import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Heading,
  Textarea,
  useToast,
  HStack,
  Stack,
  FormHelperText,
  Text,
  Tooltip,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Checkbox,
  Select,
  Switch,
} from "@chakra-ui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { apiUrl, allowWalletPreview } from "../config";
import Navbar from "../components/Navbar";
import { UserAuth } from "../contexts/AuthContext";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import PhoneNumberInput from "../components/PhoneNumberInput";
import HeroImagePicker from "../components/HeroImagePicker";
import QRCode from "react-qr-code";
import { defaultWalletCardBackgroundId } from "../assets/wallet-backgrounds";
import {
  availableLanguages,
  defaultSupportedLanguagesForNewCampaign,
  legacyFallbackSupportedLanguages,
} from "../config/supportedLanguages";
import { InfoOutlineIcon } from "@chakra-ui/icons";

const steps = [
  { title: "Contact" },
  { title: "Campaign Details" },
  { title: "Coupon Book" },
  { title: "Digital Wallet Card" },
];

const sectionHeadings = [
  "Contact Information",
  "Campaign Details",
  "Coupon Book",
  "Digital Wallet Card",
];

const DEFAULT_WALLET_TEXT_COLOR = "#000000";

const validationSchemas = [
  // Step 1: Contact Info
  Yup.object().shape({
    contactName: Yup.string().required("Contact name is required"),
    contactEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
    contactPhone: Yup.string()
      .matches(/^\d{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
  }),
  // Step 2: Campaign Details
  Yup.object().shape({
    supportedLanguages: Yup.array()
      .of(Yup.string())
      .min(1, "At least one language is required")
      .required("At least one language is required"),
    name: Yup.object().shape({
      en: Yup.string().required("Campaign name (English) is required"),
      fr: Yup.string().nullable(),
    }),
    description: Yup.object().shape({
      en: Yup.string()
        .max(1000, "Description must be at most 1000 characters")
        .required("Description (English) is required"),
      fr: Yup.string()
        .max(1000, "Description must be at most 1000 characters")
        .nullable(),
    }),
    startDate: Yup.date().nullable(),
    goal: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? null : value
      )
      .min(0, "Goal must be a positive number"),
    endDate: Yup.date()
      .required("End date is required")
      .min(new Date(), "End date must be in the future"),
  }),
  // Step 3: Coupon Book
  Yup.object().shape({
    couponBookPrice: Yup.number()
      .positive("Price must be positive")
      .required("Coupon book price is required"),
    currency: Yup.string()
      .oneOf(["CAD", "USD"], "Select a supported currency")
      .required("Currency is required"),
    secondaryButton: Yup.object().shape({
      url: Yup.string().url("Invalid URL").nullable(),
      label: Yup.object().shape({
        en: Yup.string().nullable(),
        fr: Yup.string().nullable(),
      }),
    }),
    redemptionStartDate: Yup.date()
      .required("Redemption start date is required")
      .min(new Date(), "Start date must be in the future"),
    redemptionEndDate: Yup.date()
      .required("Redemption end date is required")
      .min(Yup.ref("redemptionStartDate"), "End date must be after start date")
      .test(
        "min-after-campaign-end",
        "Redemption end date must be at least 1 month after campaign end date",
        function (value) {
          const campaignEnd = new Date(this.parent.endDate);
          const minEndDate = new Date(campaignEnd);
          minEndDate.setMonth(minEndDate.getMonth() + 1);
          return value >= minEndDate;
        }
      ),
  }),
  // Step 4: Digital Wallet Card
  Yup.object().shape({
    walletCard: Yup.object().shape({
      name: Yup.object().shape({
        en: Yup.string()
          .max(18, "Card name must be at most 18 characters")
          .required("Wallet card name (English) is required"),
        fr: Yup.string()
          .max(18, "Card name must be at most 18 characters")
          .nullable(),
      }),
      headerText1: Yup.object().shape({
        en: Yup.string()
          .max(10, "Header Text 1 must be at most 10 characters")
          .nullable(),
        fr: Yup.string()
          .max(10, "Header Text 1 must be at most 10 characters")
          .nullable(),
      }),
      headerText2: Yup.object().shape({
        en: Yup.string()
          .max(10, "Header Text 2 must be at most 10 characters")
          .nullable(),
        fr: Yup.string()
          .max(10, "Header Text 2 must be at most 10 characters")
          .nullable(),
      }),
      cardText2: Yup.object().shape({
        en: Yup.string()
          .max(30, "Card Text must be at most 30 characters")
          .nullable(),
        fr: Yup.string()
          .max(30, "Card Text must be at most 30 characters")
          .nullable(),
      }),
    }),
  }),
];

const combinedValidationSchema = validationSchemas.reduce(
  (acc, schema) => acc.concat(schema),
  Yup.object().shape({})
);

// In edit mode, dates may already be in the past for a running campaign.
// Drop the "must be in the future" check on endDate/redemptionStartDate so the
// user can update other fields (e.g. supported languages, descriptions) without
// being blocked. redemptionEndDate keeps its relative checks (after start,
// +1 month after endDate).
const editValidationSchema = combinedValidationSchema.shape({
  endDate: Yup.date().required("End date is required"),
  redemptionStartDate: Yup.date().required(
    "Redemption start date is required"
  ),
});

const CampaignForm = () => {
  const { campaignId, organizationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = UserAuth();

  const isEditMode = !!campaignId;

  // Org contact (email / phoneNumber) used to seed create-mode defaults.
  // Stays null until the org doc is fetched.
  const [orgContact, setOrgContact] = useState(null);

  // Defaults for create mode. Contact fields are seeded from the
  // organization (email/phone) and the logged-in user (name).
  const buildCreateModeDefaults = (currentUser, currentOrgContact) => ({
    supportedLanguages: defaultSupportedLanguagesForNewCampaign,
    name: { en: "", fr: "" },
    description: { en: "", fr: "" },
    contactName: currentUser
      ? `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim()
      : "",
    contactEmail: currentOrgContact?.email || currentUser?.email || "",
    contactPhone: currentOrgContact?.phoneNumber || "",
    couponBookPrice: "",
    currency: "CAD",
    secondaryButton: { url: "", label: { en: "", fr: "" } },
    startDate: "",
    goal: "",
    endDate: "",
    redemptionStartDate: "",
    redemptionEndDate: "",
    heroImage: null,
    walletCard: {
      // backgroundId/textColor retained with defaults so the (unchanged)
      // preview component keeps rendering until the preview itself is
      // refactored to the new field-based layout.
      backgroundId: defaultWalletCardBackgroundId,
      textColor: DEFAULT_WALLET_TEXT_COLOR,
      name: { en: "", fr: "" },
      headerText1: { en: "", fr: "" },
      headerText2: { en: "", fr: "" },
      cardText2: { en: "", fr: "" },
    },
  });
  const [initialValues, setInitialValues] = useState(() =>
    buildCreateModeDefaults(user, null)
  );

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [hasConfirmedDateChange, setHasConfirmedDateChange] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isTogglingActive, setIsTogglingActive] = useState(false);
  const cancelRef = React.useRef();

  // Determine whether the viewer is a campaign admin so we can show
  // admin-only controls (e.g. the Active toggle).
  useEffect(() => {
    const checkRole = async () => {
      if (!isEditMode || !user?.uid || !campaignId) return;
      try {
        const agentRef = doc(db, `campaigns/${campaignId}/agents`, user.uid);
        const agentDoc = await getDoc(agentRef);
        if (agentDoc.exists()) {
          setIsAdmin(agentDoc.data().isAdmin === true);
        } else {
          setIsAdmin(false);
        }
      } catch (error) {
        console.error("Error checking campaign admin role:", error);
        setIsAdmin(false);
      }
    };
    checkRole();
  }, [campaignId, isEditMode, user]);

  const handleActiveToggle = async () => {
    if (isTogglingActive) return;
    setIsTogglingActive(true);
    try {
      const idToken = sessionStorage.getItem("idToken");
      await axios.put(
        `${apiUrl}/campaigns/${campaignId}/toggleActive`,
        { active: !isActive },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      setIsActive(!isActive);
      toast({
        title: "Success",
        description: `Campaign ${
          !isActive ? "activated" : "deactivated"
        } successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error toggling campaign status:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to update campaign status",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsTogglingActive(false);
    }
  };

  const handlePreviewSave = async (formikProps) => {
    const errors = await formikProps.validateForm();
    if (Object.keys(errors).length > 0) {
      const touched = Object.keys(errors).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      );
      formikProps.setTouched(touched);
      toast({
        title: "Cannot save",
        description: "Please fix the form errors before saving.",
        status: "warning",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    setIsSavingPreview(true);
    try {
      const idToken = sessionStorage.getItem("idToken");
      await axios.put(
        `${apiUrl}/campaigns/${campaignId}`,
        { ...formikProps.values },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );
      toast({
        title: "Saved",
        description: "Changes saved. Re-scan the QR code to preview.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save changes.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSavingPreview(false);
    }
  };

  useEffect(() => {
    const fetchCampaign = async () => {
      if (isEditMode) {
        try {
          const campaignDoc = await getDoc(doc(db, "campaigns", campaignId));

          if (campaignDoc.exists()) {
            const campaignData = campaignDoc.data();
            setIsActive(campaignData.active === true);

            // Format dates from Firestore Timestamp to YYYY-MM-DD
            const formatDate = (timestamp) => {
              if (!timestamp) return "";
              // Check if it's a Firestore Timestamp
              if (timestamp?.toDate) {
                return timestamp.toDate().toISOString().split("T")[0];
              }
              // If it's already a date string, return it as is
              return timestamp;
            };

            setInitialValues({
              supportedLanguages:
                campaignData.supportedLanguages ||
                legacyFallbackSupportedLanguages,
              name: campaignData.name || { en: "", fr: "" },
              description: campaignData.description || { en: "", fr: "" },
              contactName: campaignData.contactName || "",
              contactEmail: campaignData.contactEmail || "",
              contactPhone: campaignData.contactPhone || "",
              couponBookPrice: campaignData.couponBookPrice || "",
              // Existing campaigns predating the currency selector were all
              // CAD — fall back to that so the dropdown isn't blank.
              currency: campaignData.currency || "CAD",
              secondaryButton: {
                url:
                  campaignData.secondaryButton?.url ||
                  campaignData.offeringURL ||
                  "",
                label: {
                  en: campaignData.secondaryButton?.label?.en || "",
                  fr: campaignData.secondaryButton?.label?.fr || "",
                },
              },
              startDate: formatDate(campaignData.startDate),
              goal: campaignData.goal ?? "",
              endDate: formatDate(campaignData.endDate),
              redemptionStartDate: formatDate(campaignData.redemptionStartDate),
              redemptionEndDate: formatDate(campaignData.redemptionEndDate),
              heroImage: campaignData.heroImage || null,
              walletCard: {
                backgroundId:
                  campaignData.walletCard?.backgroundId ||
                  defaultWalletCardBackgroundId,
                textColor: DEFAULT_WALLET_TEXT_COLOR,
                name: {
                  en:
                    campaignData.walletCard?.name?.en ||
                    campaignData.name?.en ||
                    "",
                  fr:
                    campaignData.walletCard?.name?.fr ||
                    campaignData.name?.fr ||
                    "",
                },
                headerText1: {
                  en: campaignData.walletCard?.headerText1?.en || "",
                  fr: campaignData.walletCard?.headerText1?.fr || "",
                },
                headerText2: {
                  en: campaignData.walletCard?.headerText2?.en || "",
                  fr: campaignData.walletCard?.headerText2?.fr || "",
                },
                cardText2: {
                  en: campaignData.walletCard?.cardText2?.en || "",
                  fr: campaignData.walletCard?.cardText2?.fr || "",
                },
              },
            });
          } else {
            toast({
              title: "Error",
              description: "Campaign not found",
              status: "error",
              duration: 5000,
              isClosable: true,
            });
            navigate(-1);
          }
        } catch (error) {
          console.error("Error fetching campaign:", error);
          toast({
            title: "Error",
            description: "Failed to load campaign data",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    };

    fetchCampaign();
  }, [campaignId, isEditMode]);

  // In create mode, fetch the parent organization so we can seed the
  // campaign's contact email and phone from it.
  useEffect(() => {
    if (isEditMode || !organizationId) {
      setOrgContact(null);
      return;
    }
    let cancelled = false;
    const fetchOrg = async () => {
      try {
        const orgDoc = await getDoc(doc(db, "organizations", organizationId));
        if (!cancelled && orgDoc.exists()) {
          const orgData = orgDoc.data();
          setOrgContact({
            email: orgData.email || "",
            phoneNumber: orgData.phoneNumber || "",
          });
        }
      } catch (error) {
        console.error("Error fetching organization for contact defaults:", error);
      }
    };
    fetchOrg();
    return () => {
      cancelled = true;
    };
  }, [organizationId, isEditMode]);

  // React Router can reuse this component instance when navigating between
  // /create-campaign and /edit-campaign/:id. Reset to create-mode defaults
  // whenever we enter create mode (or the seed data changes) so a stale
  // bilingual campaign loaded by a previous edit doesn't leak into the new
  // campaign's initial values.
  useEffect(() => {
    if (!isEditMode) {
      setInitialValues(buildCreateModeDefaults(user, orgContact));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, user, orgContact]);

  const handleSubmit = async (values, actions) => {
    try {
      const idToken = sessionStorage.getItem("idToken");
      const targetOrgId = organizationId || user.organizationId;
      let newCampaignId = campaignId;

      if (isEditMode) {
        await axios.put(
          `${apiUrl}/campaigns/${campaignId}`,
          {
            ...values,
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
      } else {
        const response = await axios.post(
          `${apiUrl}/addCampaign`,
          {
            ...values,
            organizationId: targetOrgId,
            userId: user.uid,
            userFirstName: user.firstName,
            userLastName: user.lastName,
            userEmail: user.email,
          },
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );
        newCampaignId = response.data.campaignId;
      }

      actions.setSubmitting(false);
      toast({
        title: "Success",
        description: `Campaign ${
          isEditMode ? "updated" : "created"
        } successfully`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      navigate(`/campaign/${newCampaignId}`);
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} campaign: `,
        error
      );
      actions.setSubmitting(false);
      toast({
        title: "Error",
        description: `Failed to ${
          isEditMode ? "update" : "create"
        } campaign. Please try again.`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDateChangeConfirmation = (values, actions) => {
    const originalStartDate = initialValues.redemptionStartDate;
    const originalEndDate = initialValues.redemptionEndDate;
    const newStartDate = values.redemptionStartDate;
    const newEndDate = values.redemptionEndDate;

    if (
      isEditMode &&
      (originalStartDate !== newStartDate || originalEndDate !== newEndDate)
    ) {
      setIsConfirmDialogOpen(true);
      setPendingSubmission({ values, actions });
    } else {
      handleSubmit(values, actions);
    }
  };

  const handleConfirmationSubmit = () => {
    if (hasConfirmedDateChange && pendingSubmission) {
      setIsConfirmDialogOpen(false);
      handleSubmit(pendingSubmission.values, pendingSubmission.actions);
      setHasConfirmedDateChange(false);
      setPendingSubmission(null);
    }
  };

  const renderStepContent = (step, formikProps) => {
    switch (step) {
      case 0:
        return (
          <VStack spacing={4}>
            <Field name="contactName">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.contactName && form.touched.contactName
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">Contact Name</FormLabel>
                  <Input {...field} placeholder="Enter contact name" />
                  <FormErrorMessage>{form.errors.contactName}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="contactEmail">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.contactEmail && form.touched.contactEmail
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">Contact Email</FormLabel>
                  <Input
                    {...field}
                    type="email"
                    placeholder="Enter contact email"
                  />
                  <FormErrorMessage>
                    {form.errors.contactEmail}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="contactPhone">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.contactPhone && form.touched.contactPhone
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">Contact Phone</FormLabel>
                  <PhoneNumberInput field={field} />
                  <FormErrorMessage>
                    {form.errors.contactPhone}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </VStack>
        );
      case 1: {
        const selectedLanguages =
          formikProps.values.supportedLanguages || [];
        const showFrench = selectedLanguages.includes("fr");
        return (
          <VStack spacing={6} width="100%" align="stretch">
            <Field name="supportedLanguages">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    !!form.errors.supportedLanguages &&
                    !!form.touched.supportedLanguages
                  }
                >
                  <HStack spacing={2}>
                    <FormLabel fontSize="sm" m={0}>
                      Supported Languages
                      <Text as="span" color="red.500" ml={1}>
                        *
                      </Text>
                    </FormLabel>
                    <Tooltip
                      label="Choose which languages this campaign supports. Language fields you don't select will be hidden when entering campaign content and coupons. English is always included."
                      fontSize="sm"
                      placement="top"
                    >
                      <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                    </Tooltip>
                  </HStack>
                  <Stack direction="row" spacing={6} wrap="wrap">
                    {availableLanguages.map((lang) => {
                      const currentValue = Array.isArray(field.value)
                        ? field.value
                        : [];
                      const isChecked =
                        lang.required || currentValue.includes(lang.code);
                      return (
                        <Checkbox
                          key={lang.code}
                          isChecked={isChecked}
                          isDisabled={lang.required}
                          onChange={(e) => {
                            let next;
                            if (e.target.checked) {
                              next = currentValue.includes(lang.code)
                                ? currentValue
                                : [...currentValue, lang.code];
                            } else {
                              next = currentValue.filter(
                                (c) => c !== lang.code
                              );
                            }
                            if (!next.includes("en")) {
                              next = ["en", ...next];
                            }
                            form.setFieldValue("supportedLanguages", next);
                          }}
                        >
                          {lang.label}
                        </Checkbox>
                      );
                    })}
                  </Stack>
                  <FormErrorMessage>
                    {form.errors.supportedLanguages}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={{ base: 4, md: 6 }}
              width="100%"
            >
              <Field name="name.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.name?.en && form.touched.name?.en}
                    isRequired
                    flex={1}
                  >
                    <FormLabel fontSize="sm">
                      {showFrench ? "Campaign Name (English)" : "Campaign Name"}
                    </FormLabel>
                    <Input
                      {...field}
                      placeholder="Enter campaign name in English"
                    />
                    <FormErrorMessage>{form.errors.name?.en}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="name.fr">
                  {({ field, form }) => (
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Campaign Name (French)</FormLabel>
                      <Input
                        {...field}
                        placeholder="Enter campaign name in French (optional)"
                      />
                      <FormErrorMessage>{form.errors.name?.fr}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={{ base: 4, md: 6 }}
              width="100%"
              alignItems="flex-start"
            >
              <Field name="description.en">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.description?.en &&
                      form.touched.description?.en
                    }
                    isRequired
                    flex={1}
                  >
                    <FormLabel fontSize="sm">
                      {showFrench ? "Description (English)" : "Description"}
                    </FormLabel>
                    <Textarea
                      {...field}
                      placeholder="Enter campaign description in English"
                      minHeight="150px"
                    />
                    <FormErrorMessage>
                      {form.errors.description?.en}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name="description.fr">
                  {({ field, form }) => (
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">Description (French)</FormLabel>
                      <Textarea
                        {...field}
                        placeholder="Enter campaign description in French (optional)"
                        minHeight="150px"
                      />
                      <FormErrorMessage>
                        {form.errors.description?.fr}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={{ base: 4, md: 6 }}
              width="100%"
              alignItems="flex-start"
            >
              <Field name="startDate">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.startDate && form.touched.startDate}
                    flex={1}
                  >
                    <HStack spacing={2}>
                      <FormLabel fontSize="sm">Campaign Start Date</FormLabel>
                      <Tooltip
                        label="The day the campaign launches. Used to track progress against the campaign goal in the Reporting tab."
                        fontSize="sm"
                        placement="top"
                      >
                        <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                      </Tooltip>
                    </HStack>
                    <Input
                      {...field}
                      value={field.value || ""}
                      type="date"
                      placeholder="Select start date"
                    />
                    <FormErrorMessage>{form.errors.startDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="goal">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.goal && form.touched.goal}
                    flex={1}
                  >
                    <HStack spacing={2}>
                      <FormLabel fontSize="sm">Campaign Goal</FormLabel>
                      <Tooltip
                        label="Fundraising target for this campaign, in dollars. Used to show progress in the Reporting tab."
                        fontSize="sm"
                        placement="top"
                      >
                        <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                      </Tooltip>
                    </HStack>
                    <Input
                      {...field}
                      value={field.value ?? ""}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g. 10000"
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                    <FormErrorMessage>{form.errors.goal}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </Stack>

            <Field name="endDate">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.endDate && form.touched.endDate}
                  isRequired
                >
                  <HStack spacing={2}>
                    <FormLabel fontSize="sm">Campaign End Date</FormLabel>
                    <Tooltip
                      label="This is the last day customers can purchase coupon books. The redemption period (chosen in the next step) must extend at least one month beyond this date."
                      fontSize="sm"
                      placement="top"
                    >
                      <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                    </Tooltip>
                  </HStack>
                  <Input
                    {...field}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    placeholder="Select end date"
                    w={{ base: "full", md: "33%" }}
                  />

                  <FormErrorMessage>{form.errors.endDate}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
          </VStack>
        );
      }
      case 2: {
        const showFrench = (
          formikProps.values.supportedLanguages || []
        ).includes("fr");
        return (
          <VStack spacing={4} width="100%" align="stretch">
            <Field name="couponBookPrice">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.couponBookPrice && form.touched.couponBookPrice
                  }
                  isRequired
                >
                  <FormLabel fontSize="sm">Coupon Book Price</FormLabel>
                  <HStack
                    spacing={2}
                    w={{ base: "full", md: "33%" }}
                    align="stretch"
                  >
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      placeholder="Enter coupon book price"
                      onWheel={(e) => e.currentTarget.blur()}
                      flex={1}
                    />
                    <Field name="currency">
                      {({ field: currencyField }) => (
                        <Select
                          {...currencyField}
                          maxW="100px"
                          flexShrink={0}
                          aria-label="Currency"
                        >
                          <option value="CAD">CAD</option>
                          <option value="USD">USD</option>
                        </Select>
                      )}
                    </Field>
                  </HStack>
                  <FormErrorMessage>
                    {form.errors.couponBookPrice}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name="redemptionStartDate">
              {({ field, form }) => (
                <FormControl
                  isInvalid={
                    form.errors.redemptionStartDate &&
                    form.touched.redemptionStartDate
                  }
                  isRequired
                >
                  <HStack spacing={2}>
                    <FormLabel fontSize="sm">
                      Coupon Redemption Start Date
                    </FormLabel>
                    <Tooltip
                      label="This is the first day customers can redeem their coupons."
                      fontSize="sm"
                      placement="top"
                    >
                      <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                    </Tooltip>
                  </HStack>
                  <Input
                    {...field}
                    type="date"
                    min={new Date().toISOString().split("T")[0]}
                    placeholder="Select redemption start date"
                    w={{ base: "full", md: "33%" }}
                  />
                  <FormErrorMessage>
                    {form.errors.redemptionStartDate}
                  </FormErrorMessage>
                </FormControl>
              )}
            </Field>

            <Field name="redemptionEndDate">
              {({ field, form }) => {
                // Calculate minimum end date (1 month after campaign end date)
                const campaignEndDate = form.values.endDate
                  ? new Date(form.values.endDate)
                  : null;
                const minEndDate = campaignEndDate
                  ? new Date(
                      campaignEndDate.setMonth(campaignEndDate.getMonth() + 1)
                    )
                  : null;

                return (
                  <FormControl
                    isInvalid={
                      form.errors.redemptionEndDate &&
                      form.touched.redemptionEndDate
                    }
                    isRequired
                  >
                    <HStack spacing={2}>
                      <FormLabel fontSize="sm">
                        Coupon Redemption End Date
                      </FormLabel>
                      <Tooltip
                        label="This is the last day customers can redeem their coupons. This date must extend at least 1 month past the campaign end date."
                        fontSize="sm"
                        placement="top"
                      >
                        <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                      </Tooltip>
                    </HStack>
                    <Input
                      {...field}
                      type="date"
                      min={
                        minEndDate ? minEndDate.toISOString().split("T")[0] : ""
                      }
                      placeholder="Select redemption end date"
                      w={{ base: "full", md: "33%" }}
                    />
                    <FormErrorMessage>
                      {form.errors.redemptionEndDate}
                    </FormErrorMessage>
                  </FormControl>
                );
              }}
            </Field>

            <VStack
              spacing={3}
              width="100%"
              align="stretch"
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
            >
              <HStack spacing={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Secondary Button (Optional)
                </Text>
                <Tooltip
                  label="Adds an extra button next to the primary purchase button on the campaign sales page — typically used to link out to a partner site or a longer description of the offering."
                  fontSize="sm"
                  placement="top"
                >
                  <InfoOutlineIcon boxSize={4} color="gray.500" />
                </Tooltip>
              </HStack>

              <Field name="secondaryButton.url">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={
                      form.errors.secondaryButton?.url &&
                      form.touched.secondaryButton?.url
                    }
                  >
                    <FormLabel fontSize="sm">Link URL</FormLabel>
                    <Input
                      {...field}
                      value={field.value || ""}
                      type="url"
                      placeholder="https://example.com"
                    />
                    <FormErrorMessage>
                      {form.errors.secondaryButton?.url}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={{ base: 3, md: 4 }}
                width="100%"
                alignItems="flex-start"
              >
                <Field name="secondaryButton.label.en">
                  {({ field }) => (
                    <FormControl flex={1}>
                      <FormLabel fontSize="sm">
                        {showFrench
                          ? "Button Text (English)"
                          : "Button Text"}
                      </FormLabel>
                      <Input
                        {...field}
                        value={field.value || ""}
                        placeholder="Learn More"
                      />
                    </FormControl>
                  )}
                </Field>
                {showFrench && (
                  <Field name="secondaryButton.label.fr">
                    {({ field }) => (
                      <FormControl flex={1}>
                        <FormLabel fontSize="sm">Button Text (French)</FormLabel>
                        <Input
                          {...field}
                          value={field.value || ""}
                          placeholder="En savoir plus"
                        />
                      </FormControl>
                    )}
                  </Field>
                )}
              </Stack>
            </VStack>

            <Field name="heroImage">
              {({ field, form }) => (
                <FormControl>
                  <HStack spacing={2}>
                    <FormLabel fontSize="sm" m={0}>
                      Sales Page Hero Image (Optional)
                    </FormLabel>
                    <Tooltip
                      label={
                        <Box>
                          <Text>
                            Optional. The image becomes the background of the
                            hero section at the top of the campaign purchase
                            page, with a dark overlay for text readability.
                          </Text>
                          <Text mt={2}>
                            Recommended: landscape orientation, at least
                            1920&times;600&nbsp;px (16:5 aspect).
                          </Text>
                          <Text mt={2}>
                            Accepted formats: JPG, PNG, or WebP under
                            10&nbsp;MB.
                          </Text>
                          <Text mt={2}>
                            High-contrast scenes without dense text work best.
                          </Text>
                        </Box>
                      }
                      fontSize="sm"
                      placement="top"
                    >
                      <InfoOutlineIcon boxSize={4} color="gray.500" mb={2} />
                    </Tooltip>
                  </HStack>
                  <HeroImagePicker
                    value={field.value}
                    onChange={(next) =>
                      form.setFieldValue("heroImage", next)
                    }
                  />
                </FormControl>
              )}
            </Field>
          </VStack>
        );
      }
      case 3: {
        const walletErrors = formikProps.errors.walletCard || {};
        const walletTouched = formikProps.touched.walletCard || {};
        const showFrench = (
          formikProps.values.supportedLanguages || []
        ).includes("fr");

        // Renders a bilingual (en + fr when supported) text input pair for a
        // wallet card field. Keeps Step 4 markup compact since every field
        // follows the same shape.
        const renderBilingualField = ({
          baseName,
          label,
          maxLength,
          required = false,
          placeholderEn = "",
          placeholderFr = "",
        }) => {
          const errs = walletErrors[baseName] || {};
          const touched = walletTouched[baseName] || {};
          return (
            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={{ base: 4, md: 6 }}
              width="100%"
              // Each wallet field is ~1/2 form width. With French shown, the
              // row holds two flex={1} fields and naturally fills 100% (each
              // ~50%). With French hidden, cap the row at 50% so the single
              // English field doesn't stretch full width.
              maxW={
                showFrench ? "100%" : { base: "100%", md: "50%" }
              }
            >
              <Field name={`walletCard.${baseName}.en`}>
                {({ field }) => (
                  <FormControl
                    isInvalid={errs.en && touched.en}
                    isRequired={required}
                    flex={1}
                  >
                    <FormLabel fontSize="sm">
                      {showFrench ? `${label} (English)` : label}
                    </FormLabel>
                    <Input
                      {...field}
                      placeholder={placeholderEn}
                      maxLength={maxLength}
                    />
                    <FormHelperText>
                      Up to {maxLength} characters.
                    </FormHelperText>
                    <FormErrorMessage>{errs.en}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              {showFrench && (
                <Field name={`walletCard.${baseName}.fr`}>
                  {({ field }) => (
                    <FormControl isInvalid={errs.fr && touched.fr} flex={1}>
                      <FormLabel fontSize="sm">{`${label} (French)`}</FormLabel>
                      <Input
                        {...field}
                        placeholder={placeholderFr}
                        maxLength={maxLength}
                      />
                      <FormHelperText>
                        Up to {maxLength} characters.
                      </FormHelperText>
                      <FormErrorMessage>{errs.fr}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              )}
            </Stack>
          );
        };

        return (
          <VStack spacing={6} width="100%" align="stretch">
            <Text fontSize="sm" color="gray.600">
              Configure the text shown on this campaign's digital wallet card.
            </Text>

            {renderBilingualField({
              baseName: "name",
              label: "Card Name",
              maxLength: 18,
              required: true,
              placeholderEn: "e.g. Chelsea Coupons",
              placeholderFr: "e.g. Chelsea Coupons",
            })}

            {renderBilingualField({
              baseName: "headerText1",
              label: "Header Text 1",
              maxLength: 10,
              placeholderEn: "e.g. Summer",
              placeholderFr: "e.g. Summer",
            })}

            {renderBilingualField({
              baseName: "headerText2",
              label: "Header Text 2",
              maxLength: 10,
              placeholderEn: "e.g. 2026",
              placeholderFr: "e.g. 2026",
            })}

            {renderBilingualField({
              baseName: "cardText2",
              label: "Card Text",
              maxLength: 30,
              placeholderEn: "e.g. Thank-you for supporting us!",
              placeholderFr: "e.g. Thank-you for supporting us!",
            })}

            {allowWalletPreview && (
              <Box pt={2}>
                <VStack spacing={2} align="flex-start">
                  <Text fontSize="sm" fontWeight="medium">
                    Preview on iPhone
                  </Text>
                  {campaignId ? (
                    <>
                      <Text fontSize="sm" color="gray.600" textAlign="left">
                        Save first, and then scan with an iPhone to preview the
                        Apple Wallet card.
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => handlePreviewSave(formikProps)}
                        isLoading={isSavingPreview}
                      >
                        Save
                      </Button>
                      <Box bg="white" p={3} borderWidth="1px" borderRadius="md">
                        <QRCode
                          value={`${apiUrl}/wallet/apple/${campaignId}/000000`}
                          size={160}
                        />
                      </Box>
                    </>
                  ) : (
                    <Text fontSize="sm" color="gray.600" textAlign="left">
                      The QR code will appear here once the campaign is saved.
                    </Text>
                  )}
                </VStack>
              </Box>
            )}
          </VStack>
        );
      }
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />
      <Box
        minH="calc(100vh - 60px)"
        bg="primaryBackground"
        pt={{ base: "20px", md: "60px" }}
        px={{ base: 4, md: 6, lg: 8 }}
      >
        <VStack
          align="center"
          maxWidth="800px"
          margin="0 auto"
          pt={{ base: 4, md: 8 }}
        >
          <Heading
            as="h1"
            size={{ base: "md", md: "lg" }}
            textAlign="center"
            color="heading"
            pb={{ base: 2, md: 4 }}
          >
            {isEditMode ? "Edit Campaign" : "Create a Campaign"}
          </Heading>

          {isEditMode && isAdmin && (
            <HStack
              justify="center"
              align="center"
              spacing={3}
              pb={{ base: 3, md: 4 }}
            >
              <Switch
                isChecked={isActive}
                onChange={handleActiveToggle}
                isDisabled={isTogglingActive}
                colorScheme="green"
              />
              <Text color="gray.600">
                {isActive ? "Active" : "Inactive"}
              </Text>
            </HStack>
          )}

          <Box
            width="100%"
            bg="secondaryBackground"
            p={{ base: 4, sm: 6, md: 8, lg: 12 }}
            borderRadius="xl"
            boxShadow="md"
          >
            <Formik
              initialValues={initialValues}
              validationSchema={
                isEditMode ? editValidationSchema : combinedValidationSchema
              }
              onSubmit={async (values, actions) => {
                await handleDateChangeConfirmation(values, actions);
              }}
              enableReinitialize
            >
              {(props) => (
                <Form>
                  <VStack spacing={{ base: 6, md: 8 }} width="100%">
                    <VStack
                      spacing={{ base: 8, md: 10 }}
                      width="100%"
                      align="stretch"
                    >
                      {steps.map((_, idx) => (
                        <Box key={idx} width="100%">
                          <Heading
                            as="h2"
                            size="md"
                            color="heading"
                            pb={{ base: 3, md: 4 }}
                            borderBottomWidth="1px"
                            borderColor="gray.200"
                            mb={{ base: 4, md: 6 }}
                          >
                            {sectionHeadings[idx]}
                          </Heading>
                          {renderStepContent(idx, props)}
                        </Box>
                      ))}
                    </VStack>

                    <HStack
                      justify="flex-end"
                      width="100%"
                      pt={{ base: 4, md: 6 }}
                      flexDir={{ base: "column", sm: "row" }}
                      spacing={{ base: 4, sm: 0 }}
                    >
                      <Button
                        variant="secondary"
                        type="submit"
                        isLoading={props.isSubmitting}
                        width={{ base: "100%", sm: "auto" }}
                      >
                        {isEditMode ? "Update Campaign" : "Create Campaign"}
                      </Button>
                    </HStack>
                  </VStack>
                </Form>
              )}
            </Formik>
          </Box>
        </VStack>
      </Box>

      <AlertDialog
        isOpen={isConfirmDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setIsConfirmDialogOpen(false);
          setHasConfirmedDateChange(false);
          if (pendingSubmission) {
            pendingSubmission.actions.setSubmitting(false);
          }
          setPendingSubmission(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirm Redemption Date Changes
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing={4}>
                <Text>
                  Warning: You are about to change the redemption period for
                  this campaign. This will affect when customers can redeem
                  their coupons. Participating businesses must be notified and
                  agree to this change prior to the change being made.
                </Text>
                <Text fontWeight="bold">
                  Original period: {initialValues.redemptionStartDate} to{" "}
                  {initialValues.redemptionEndDate}
                </Text>
                <Text fontWeight="bold">
                  New period: {pendingSubmission?.values.redemptionStartDate} to{" "}
                  {pendingSubmission?.values.redemptionEndDate}
                </Text>
                <Checkbox
                  isChecked={hasConfirmedDateChange}
                  onChange={(e) => setHasConfirmedDateChange(e.target.checked)}
                >
                  I certify that I have notified all participating businesses of
                  this change and they have agreed to it.
                </Checkbox>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                variant="ghost"
                onClick={() => {
                  setIsConfirmDialogOpen(false);
                  setHasConfirmedDateChange(false);
                  if (pendingSubmission) {
                    pendingSubmission.actions.setSubmitting(false);
                  }
                  setPendingSubmission(null);
                }}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleConfirmationSubmit}
                ml={3}
                isDisabled={!hasConfirmedDateChange}
              >
                Confirm Changes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default CampaignForm;
